import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import UIButton from 'UIComponents/button/UIButton';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { Popover } from 'foundations-components/transitional/components/Popover';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { getAvailableCurrencies } from '../../selectors/multiCurrencySelectors';
import { getIsDealStageView } from '../../selectors/forecastSelectors';
import useFetchPreferredCurrency from './hooks/useFetchPreferredCurrency';
import useDeletePreferredCurrency from './hooks/useDeletePreferredCurrency';
import { trackDismissSetCurrencyAlert, trackOpenMulticurrencyPanel } from '../../utils/usageTracker';
import { deriveSetCurrencyAlertDismissedStorageValue, setSetCurrencyAlertDismissedStorageValue } from '../../utils/alertsUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SetCurrencyButton = ({
  isDetail,
  isUngatedToFMO,
  setIsMultiCurrencyPanelOpen
}) => {
  const isSetCurrencyAlertDismissed = deriveSetCurrencyAlertDismissedStorageValue();
  const [isPopoverOpen, setIsPopoverOpen] = useState(!isSetCurrencyAlertDismissed);
  const availableCurrencies = useSelector(getAvailableCurrencies);
  const isDealStageView = useSelector(getIsDealStageView);
  const {
    data: preferredCurrency
  } = useFetchPreferredCurrency();
  const deletePreferredCurrency = useDeletePreferredCurrency({
    setIsMultiCurrencyPanelOpen
  });
  const [hasCheckedIfCurrencyIsArchived, setHasCheckedIfCurrencyIsArchived] = useState(false);
  const handleSetCurrencyAlertDismissed = useCallback(() => {
    setSetCurrencyAlertDismissedStorageValue();
    trackDismissSetCurrencyAlert(isDetail);
  }, [isDetail]);
  const handleOpenChange = useCallback(e => {
    setIsPopoverOpen(e.target.value);
  }, [setIsPopoverOpen]);
  useEffect(() => {
    if (preferredCurrency && !hasCheckedIfCurrencyIsArchived) {
      if (availableCurrencies && availableCurrencies.length > 0 && !availableCurrencies.includes(preferredCurrency)) {
        deletePreferredCurrency()
        // errors are caught in useDeletePreferredCurrency hook
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        .catch(() => {});
      }
      setHasCheckedIfCurrencyIsArchived(true);
    }
  }, [availableCurrencies, deletePreferredCurrency, hasCheckedIfCurrencyIsArchived, preferredCurrency]);
  return /*#__PURE__*/_jsx(Popover, {
    closeOnOutsideClick: false,
    closeOnTargetLeave: false,
    open: isPopoverOpen,
    onOpenChange: handleOpenChange,
    openOnTargetEnter: false,
    content: {
      header: /*#__PURE__*/_jsx(Text, {
        as: "strong",
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "index.header.actions.setCurrencyTooltip.header"
        })
      }),
      footer: /*#__PURE__*/_jsxs(Flex, {
        inlineSize: "300px",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: isUngatedToFMO ? 'index.header.actions.setCurrencyTooltip.bodyFmoEnabled' : 'index.header.actions.setCurrencyTooltip.bodyFmoDisabled'
        }), /*#__PURE__*/_jsx(Illustration, {
          name: "multiple-currency-revenue",
          width: isUngatedToFMO ? 80 : 60
        })]
      })
    },
    onCloseComplete: handleSetCurrencyAlertDismissed,
    placement: isUngatedToFMO ? 'left' : 'bottom' // avoid obfuscating other dropdown options when FMO is on
    ,
    showCloseButton: true,
    children: /*#__PURE__*/_jsx(UIButton, {
      "data-test-id": "set-currency-button",
      onClick: () => {
        setIsMultiCurrencyPanelOpen(true);
        trackOpenMulticurrencyPanel({
          isDealStageView,
          isDetail
        });
      },
      size: "small",
      use: "secondary",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "index.header.actions.setCurrency"
      })
    })
  });
};
export default SetCurrencyButton;